import { isNotNilOrEmpty } from "client/Misc/utils";
import { Node as ProsemirrorNode } from "prosemirror-model";
import { updateColumnsOnResize } from "prosemirror-tables";
import { NodeView } from "prosemirror-view";
import {
	applyTableStyleAttributesToElement,
	TableNodeAttributes
} from "../schema/nodes/table";

export class StyledTableView implements NodeView {
	private readonly _styleObserver: MutationObserver;
	private _node: ProsemirrorNode;
	private readonly _cellMinWidth: number;
	private _widthFromAttributes: string | undefined;

	public readonly dom: HTMLDivElement;
	public readonly table: HTMLTableElement;
	public readonly colgroup: HTMLTableColElement;
	public readonly contentDOM: HTMLTableSectionElement;

	constructor(
		node: ProsemirrorNode,
		cellMinWidth: number
	) {
		this._node = node;

		this._cellMinWidth = cellMinWidth;

		this.dom = document.createElement("div");
		this.dom.className = "tableWrapper";

		this.table = this.dom.appendChild(
			document.createElement("table")
		);

		this.colgroup = this.table.appendChild(
			document.createElement("colgroup")
		);

		const attrs = node.attrs as TableNodeAttributes;
		this._widthFromAttributes = attrs.width;

		this._styleObserver = new MutationObserver(
			(mutations: MutationRecord[]): void => {
				if (mutations.some(
					m => m.type === "attributes"
						&& m.attributeName === "style"
						&& m.target === this.table
				)) {
					this.ensureTableWidth();
				}
			}
		);

		this._styleObserver
			.observe(
				this.table,
				{
					attributes: true,
					attributeFilter: ["style"]
				}
			);

		applyTableStyleAttributesToElement(
			this.table,
			attrs
		);

		updateColumnsOnResize(
			node,
			this.colgroup,
			this.table,
			cellMinWidth
		);

		this.contentDOM = this.table.appendChild(
			document.createElement("tbody")
		);
	}

	public update(node: ProsemirrorNode): boolean {
		if (node.type !== this._node.type) {
			return false;
		}

		this._node = node;

		const attrs = node.attrs as TableNodeAttributes;
		this._widthFromAttributes = attrs.width;

		applyTableStyleAttributesToElement(
			this.table,
			attrs
		);

		updateColumnsOnResize(
			node,
			this.colgroup,
			this.table,
			this._cellMinWidth
		);

		return true;
	}

	public ignoreMutation(record: MutationRecord | SelectionMutationRecord): boolean {
		if (record.type !== "attributes") {
			return false;
		}

		return record.target === this.table
			|| this.colgroup
				.contains(record.target);
	}

	public destroy(): void {
		this._styleObserver
			.disconnect();
	}

	private ensureTableWidth(): void {
		if (!this._widthFromAttributes) {
			return;
		}

		const tableStyle: CSSStyleDeclaration = this.table.style;

		if (isNotNilOrEmpty(tableStyle.width)) {
			return;
		}

		tableStyle.width = this._widthFromAttributes;
	}
}

type SelectionMutationRecord = {
	type: "selection";
	target: Element;
};